// Query hooks for the belt/ranking profile management UI (#746).
//
// All reads use RLS as defined in supabase/migrations/20260516000000_create_belt_schema.sql:
//   - System templates (is_system=true) are world-readable to authenticated.
//   - Company-owned profiles are scoped via public.user_in_company.
// belt_ranks rides on its parent belt_profiles policy.

import { useQuery } from '@tanstack/react-query';
import { supabase } from '../../lib/supabase';
import { useCurrentCompany } from '../useCurrentCompany';
import type {
  BeltProfile,
  BeltProfileWithRanks,
  BeltRank,
} from '../../types/belts';

export const beltsQueryKeys = {
  all: ['belts'] as const,
  profiles: () => [...beltsQueryKeys.all, 'profiles'] as const,
  profile: (id: string) => [...beltsQueryKeys.all, 'profile', id] as const,
  rankReferences: (profileId: string) =>
    [...beltsQueryKeys.all, 'rank-references', profileId] as const,
};

/**
 * Lists every belt profile visible to the current owner: all system templates
 * (read-only menu to clone from) and every company-owned profile for the
 * active company. Split client-side by `is_system`.
 */
export function useBeltProfiles() {
  const { data: companyId } = useCurrentCompany();

  return useQuery({
    queryKey: [...beltsQueryKeys.profiles(), companyId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('belt_profiles')
        .select('*')
        .order('is_system', { ascending: false })
        .order('name', { ascending: true });

      if (error) throw error;
      return (data ?? []) as BeltProfile[];
    },
    staleTime: 60_000,
  });
}

/**
 * Loads a single profile with all its ranks (ordered by rank_order). Returns
 * null when not found. Editable for company-owned profiles only — RLS blocks
 * writes against system templates.
 */
export function useBeltProfile(profileId: string | undefined) {
  return useQuery({
    queryKey: profileId
      ? beltsQueryKeys.profile(profileId)
      : [...beltsQueryKeys.all, 'profile', 'none'],
    queryFn: async () => {
      if (!profileId) return null;

      // Two queries (instead of select('*, belt_ranks(*)')) — the embedded
      // relation form triggers an excessively-deep TS instantiation on the
      // current supabase-js client typings.
      const [profileRes, ranksRes] = await Promise.all([
        supabase.from('belt_profiles').select('*').eq('id', profileId).maybeSingle(),
        supabase
          .from('belt_ranks')
          .select('*')
          .eq('profile_id', profileId)
          .order('rank_order', { ascending: true }),
      ]);

      if (profileRes.error) throw profileRes.error;
      if (ranksRes.error) throw ranksRes.error;
      if (!profileRes.data) return null;

      const profile = profileRes.data as BeltProfile;
      const belt_ranks = (ranksRes.data ?? []) as BeltRank[];
      return { ...profile, belt_ranks } as BeltProfileWithRanks;
    },
    enabled: !!profileId,
    staleTime: 30_000,
  });
}

/**
 * Counts how many student_ranks reference any of this profile's belt_ranks.
 * Drives the delete-confirmation UX — a profile with active student references
 * can't be deleted (FK on student_ranks.current_rank_id is NO ACTION).
 */
export function useBeltProfileRankReferences(profileId: string | undefined) {
  return useQuery({
    queryKey: profileId
      ? beltsQueryKeys.rankReferences(profileId)
      : [...beltsQueryKeys.all, 'rank-references', 'none'],
    queryFn: async () => {
      if (!profileId) return 0;

      const { count, error } = await supabase
        .from('student_ranks')
        .select('id', { count: 'exact', head: true })
        .in(
          'current_rank_id',
          (
            await supabase
              .from('belt_ranks')
              .select('id')
              .eq('profile_id', profileId)
          ).data?.map((r) => r.id) ?? []
        );

      if (error) throw error;
      return count ?? 0;
    },
    enabled: !!profileId,
    staleTime: 30_000,
  });
}
